const API_URL = import.meta.env.VITE_API_URL ?? "/api/v1";
const CSRF_COOKIE_NAME = import.meta.env.VITE_CSRF_COOKIE_NAME ?? "well_csrf";

function readCookie(name) {
  const prefix = `${encodeURIComponent(name)}=`;
  const item = document.cookie.split("; ").find((cookie) => cookie.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : null;
}

export async function apiRequest(path, options = {}) {
  const method = (options.method ?? "GET").toUpperCase();
  const businessSegment = localStorage.getItem("well_active_segment");
  const csrfToken = readCookie(CSRF_COOKIE_NAME);
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(businessSegment ? { "X-Business-Segment": businessSegment } : {}),
      ...(!["GET", "HEAD", "OPTIONS"].includes(method) && csrfToken
        ? { "X-CSRF-Token": csrfToken }
        : {}),
      ...options.headers
    }
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    if (response.status === 401 && !["/auth/login", "/auth/mfa/verify"].includes(path)) {
      window.dispatchEvent(new Event("well:session-expired"));
    }
    throw Object.assign(new Error(error.detail ?? error.message ?? "Falha na API"), {
      status: response.status,
      error
    });
  }
  return response.status === 204 ? null : response.json();
}

export function login(credentials) {
  return apiRequest("/auth/login", {
    method: "POST",
    body: JSON.stringify(credentials)
  });
}

export const getBootstrapStatus = (tenant = "well") => apiRequest(`/auth/bootstrap/status?tenant=${encodeURIComponent(tenant)}`);
export const createBootstrapAdmin = (payload) => apiRequest("/auth/bootstrap", { method: "POST", body: JSON.stringify(payload) });

export function getCurrentSession() {
  return apiRequest("/auth/me");
}

export function verifyMfa(challengeToken, code) {
  return apiRequest("/auth/mfa/verify", {
    method: "POST",
    body: JSON.stringify({ challenge_token: challengeToken, code })
  });
}

export function logout() {
  return apiRequest("/auth/logout", { method: "POST" });
}

export function listVehicles() {
  return apiRequest("/operations/technical-vehicles");
}

export function createTechnicalRecord(payload) {
  return apiRequest("/operations/technical-records", { method: "POST", body: JSON.stringify(payload) });
}

export function listTechnicalRecords(params = {}) {
  const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""));
  return apiRequest(`/operations/technical-records${query.size ? `?${query}` : ""}`);
}

export function generateVehicleDailyReport(payload) {
  return apiRequest("/operations/vehicle-daily-reports/generate", { method: "POST", body: JSON.stringify(payload) });
}

export const listAccessUsers = () => apiRequest("/access/users");
export const listAccessProfiles = () => apiRequest("/access/profiles");
export const createAccessUser = (payload) => apiRequest("/access/users", { method: "POST", body: JSON.stringify(payload) });
export const updateAccessUser = (id, payload) => apiRequest(`/access/users/${id}/access`, { method: "PUT", body: JSON.stringify(payload) });
export const listCompanies = () => apiRequest("/tenants");
export const createCompany = (payload) => apiRequest("/tenants", { method: "POST", body: JSON.stringify(payload) });
export const createCompanyAdministrator = (tenantId, payload) => apiRequest(`/tenants/${tenantId}/administrator`, { method: "POST", body: JSON.stringify(payload) });
export const listClients = () => apiRequest("/clients");
export const createClient = (payload) => apiRequest("/clients", { method: "POST", body: JSON.stringify(payload) });
export const getClientStructureOverview = (clientId) => apiRequest(`/client-structure/overview${clientId ? `?client_id=${encodeURIComponent(clientId)}` : ""}`);
export const createClientSector = (payload) => apiRequest("/client-structure/sectors", { method: "POST", body: JSON.stringify(payload) });
export const createClientRoom = (payload) => apiRequest("/client-structure/rooms", { method: "POST", body: JSON.stringify(payload) });
export const createNetworkPoint = (payload) => apiRequest("/client-structure/points", { method: "POST", body: JSON.stringify(payload) });
export const createPatchPanel = (payload) => apiRequest("/client-structure/patch-panels", { method: "POST", body: JSON.stringify(payload) });
export const createClientConnectionRecord = (payload) => apiRequest("/client-structure/connections", { method: "POST", body: JSON.stringify(payload) });
export const listServiceOrders = () => apiRequest("/service-orders");
export const createServiceOrder = (payload) => apiRequest("/service-orders", { method: "POST", body: JSON.stringify(payload) });
export const listServiceOrderInteractions = (id) => apiRequest(`/service-orders/${id}/interactions`);
export const replyServiceOrder = (id, payload) => apiRequest(`/service-orders/${id}/interactions`, { method: "POST", body: JSON.stringify(payload) });
export const createServiceOrderEvent = (id, payload) => apiRequest(`/service-orders/${id}/events`, { method: "POST", body: JSON.stringify(payload) });
